import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs';

import { FeatureFlagKeys, selectFeatureFlag } from '@ninety/platform/state';
import { BillingStateActions } from '@ninety/platform/state/app-global/billing/billing-state.actions';
import { selectIsThriveOrFreemiumPlan } from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { TerraIconModule, TerraSwitchModule } from '@ninety/terra';
import { ButtonComponent, NinetyChipComponent, SkeletonComponent } from '@ninety/ui/core';
import {
  GoogleCalendarEvent,
  NativeIntegration,
  NativeIntegrationCoreService,
} from '@ninety/web/pages/shared/integrations';

/**
 * The "Record and transcribe this meeting" card shared by the start/schedule/update meeting
 * dialogs: Thrive/Freemium entitlement gate, the toggle, connect-calendar button, and the picked/existing-event
 * display. Does not render the event picker itself - emits `openPicker` and expects the host
 * dialog to swap its own content for `SelectCalendarEventDialogComponent` (avoids a "modal inside
 * a modal"), then feed the result back in via `pickedEvent`.
 */
@Component({
  selector: 'ninety-meeting-bot-calendar-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TerraIconModule,
    TerraSwitchModule,
    ButtonComponent,
    SkeletonComponent,
    NinetyChipComponent,
  ],
  templateUrl: './meeting-bot-calendar-section.component.html',
  styles: [
    `
      @use 'terra';

      :host {
        display: contents;
      }

      .google-calendar {
        width: 100%;
        margin-bottom: 16px;
        grid-column: 1 / -1;
      }

      .meeting-agent-section {
        display: flex;
        flex-direction: column;
        gap: 16px;
        background: terra.color('neutral-shader', 'light');
        @include terra.radius(medium);
        padding: 12px;
      }

      .meeting-agent-intro {
        display: flex;
        gap: 8px;
      }

      .meeting-agent-intro-icon {
        color: terra.color('brand');
        flex-shrink: 0;
      }

      .meeting-agent-intro-copy {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .meeting-agent-intro-title {
        @include terra.text('body-bold');
        margin: 0;

        ninety-chip {
          margin-left: 8px;
        }
      }

      .meeting-agent-intro-subtext {
        margin: 0;
      }

      .meeting-agent-upsell-text {
        margin: 0;
      }

      .add-meeting-agent-row {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .add-meeting-agent-subtext {
        margin: 0;
        // Roughly aligns under the switch's own label text rather than its track.
        padding-left: 44px;
      }

      .meeting-link-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 100%;

        ninety-button {
          margin-bottom: 4px;
        }

        ninety-skeleton {
          display: block;
          width: 100%;
        }
      }

      .calendar-event-selected {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        padding: 8px 12px;
        background: terra.color('brand', 'shader-light');
        border: 1px solid terra.color('brand', 'shader-heavy');
        @include terra.radius(small);
      }

      .calendar-event-selected-info {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
      }

      .calendar-event-selected-title {
        @include terra.text('body-semibold');
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .calendar-event-selected-meta {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }

      .change-event-button {
        background: none;
        border: none;
        padding: 0;
        flex-shrink: 0;
        cursor: pointer;
        color: terra.color('brand');

        &:hover {
          text-decoration: underline;
        }
      }

      .text-light {
        color: terra.color('text', 'light');
      }
    `,
  ],
})
export class MeetingBotCalendarSectionComponent {
  private readonly store = inject(Store);
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly destroyRef = inject(DestroyRef);

  /** Prefix for this instance's `data-cy` hooks, e.g. `'start-meeting-dialog'`. */
  readonly dataCyPrefix = input.required<string>();

  /** Host-owned control - the host also reads its value for its own create/update payload. */
  readonly addMeetingAgentControl = input.required<FormControl<boolean | null>>();

  /** The event the host dialog currently has selected, if any. */
  readonly pickedEvent = input<GoogleCalendarEvent | undefined>(undefined);

  /** A previously-saved meeting link with no event metadata (e.g. editing an existing schedule). */
  readonly existingMeetingLink = input<string | null | undefined>(undefined);

  readonly eventPickerLabel = input<string>('Select calendar event');

  /** Emitted when the user wants to choose/change the calendar event - host should show the picker. */
  readonly openPicker = output<void>();

  /** Emitted when more than one calendar provider is available to connect - host should swap in
   *  ChooseCalendarProviderDialogComponent, same "avoid a modal inside a modal" pattern as openPicker. */
  readonly openProviderChooser = output<NativeIntegration[]>();

  readonly calendarConnectEnabled$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.webCalendarConnect));
  readonly isEntitledTier$ = this.store.select(selectIsThriveOrFreemiumPlan);

  /** Every calendar-category integration available to connect (e.g. Google, Microsoft) - gated
   *  entirely behind FeatureFlagKeys.webCalendarConnect via the template's outer @if, so every
   *  provider here shares that same flag rather than needing its own. */
  readonly calendarIntegrations = computed(() => this.nativeIntegrationService.calendarIntegrations());

  readonly activeCalendarConnectionId = computed(() => this.nativeIntegrationService.activeCalendarConnectionId());

  readonly isCalendarConnected = computed(() => !!this.activeCalendarConnectionId());

  /** True until the initial `fetchNativeIntegrations()` call resolves - avoids flashing "Connect
   *  my calendar" for a calendar that's actually already connected. */
  readonly calendarStatusLoading = computed(() => this.nativeIntegrationService.loading());

  readonly connectingCalendar = signal(false);

  connectCalendar(): void {
    const integrations = this.calendarIntegrations();
    if (integrations.length === 0) {
      return;
    }

    if (integrations.length > 1) {
      this.openProviderChooser.emit(integrations);
      return;
    }

    this.connectingCalendar.set(true);
    this.nativeIntegrationService
      .connectCalendarViaPopup(integrations[0])
      .pipe(
        finalize(() => this.connectingCalendar.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  openPricingPlansModal(): void {
    this.store.dispatch(BillingStateActions.openPricingPlansModal());
  }
}
